// ─────────────────────────────────────────────────────────────────────────────
// NetworkCard — the "Global Network Map" panel. Its job is the header, the two
// views, and deciding which of three things the body shows:
//
//   ./NodeMap   — Map View:  dotted world map + a marker per node
//   ./ListView  — List View: search/filter toolbar + sortable table
//   ErrorPanel  — we have no fleet data and can't get any
//
// Both views are self-contained folders with the same shape (an index.js that
// composes a toolbar and a body), both get the same handlers, and both show
// Add Node. Everything either needs about search/filter/sort travels in one
// `filters` bundle — the object useNodeFilters returns — so nothing has to be
// unpacked and re-threaded on the way down.
//
// The error split matters: an outage with nothing cached replaces the body, but
// an outage on top of rows we already have only adds a banner. Throwing away
// good data because a refresh failed is worse than showing it slightly stale.
// A failing /enterprise-nodes/summary alone is banner-only — the KPI tiles go to
// "—" and the table is untouched, so there is nothing to block.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./NetworkCard.module.css";
import CardHeader from "./CardHeader";
import NodeMap from "./NodeMap";
import ListView from "./ListView";
import { ErrorPanel, ErrorBanner } from "./FetchState";

export default function NetworkCard({
  view, onView,            // "map" | "list"
  filters, loading,        // data + list controls
  error, hasLoaded,        // fleet fetch state
  notice,                  // anything worth a banner (fleet OR summary failure)
  selectedId, onSelect,    // row/marker selection
  onRefresh, onAddNode,    // header + toolbar actions
  onChanged,               // a write landed — refetch the fleet
  canWrite = false,        // read_write on node_monitoring: show CRUD controls
}) {
  const isBlocked = Boolean(error) && !hasLoaded;   // nothing to fall back on

  return (
    <div className={styles.card}>
      <CardHeader view={view} onView={onView} onRefresh={onRefresh} />

      {/* Not shown when blocked — the panel below already says it, in full. */}
      {notice && !isBlocked && <ErrorBanner message={notice} onRetry={onRefresh} />}

      {isBlocked ? (
        <ErrorPanel message={error} onRetry={onRefresh} />
      ) : view === "map" ? (
        <NodeMap
          nodes={filters.rows}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddNode={onAddNode}
          canWrite={canWrite}
        />
      ) : (
        <ListView
          filters={filters}
          loading={loading}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddNode={onAddNode}
          onChanged={onChanged}
          canWrite={canWrite}
        />
      )}
    </div>
  );
}
